import React, { useState } from 'react';
import { healthAPI } from '../api';
import { MoodMetadata } from '../types';
import './MoodPrompt.css';

interface MoodPromptProps {
    onComplete: () => void;
    lastEventType?: string;
    relatedTraceId?: string;
}

export const MoodPrompt: React.FC<MoodPromptProps> = ({ onComplete, lastEventType, relatedTraceId }) => {
    const [valence, setValence] = useState<number | null>(null);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const moodEmojis = [
        { value: 1, emoji: '😫', label: 'Awful' },
        { value: 2, emoji: '😕', label: 'Bad' },
        { value: 3, emoji: '😐', label: 'Okay' },
        { value: 4, emoji: '🙂', label: 'Good' },
        { value: 5, emoji: '🤩', label: 'Great' },
    ];

    const commonTags = ['Tired', 'Energetic', 'Anxious', 'Calm', 'Stressed', 'Happy', 'Bloated', 'Hungry'];

    const toggleTag = (tag: string) => {
        if (selectedTags.includes(tag)) {
            setSelectedTags(selectedTags.filter(t => t !== tag));
        } else {
            setSelectedTags([...selectedTags, tag]);
        }
    };

    const handleSubmit = async () => {
        if (valence === null) return;

        setIsSubmitting(true);
        try {
            const data: MoodMetadata = {
                valence,
                tags: selectedTags,
                related_trace_id: relatedTraceId
            };

            await healthAPI.createEvent({
                type: 'mood',
                data: data
            });

            // Update local storage for throttling
            localStorage.setItem('lastMoodTimestamp', Date.now().toString());
            onComplete();
        } catch (error) {
            console.error('Failed to log mood:', error);
            // Close anyway to not block user
            onComplete();
        } finally {
            setIsSubmitting(false);
        }
    };

    const getGreeting = () => {
        if (lastEventType === 'meal') return "Thanks for logging your meal!";
        if (lastEventType === 'bathroom') return "Health event logged.";
        if (lastEventType === 'water') return "Hydration tracked!";
        return "Checking in...";
    };

    return (
        <div className="mood-prompt-overlay">
            <div className="mood-prompt-modal">
                <h3>{getGreeting()}</h3>
                <p>How are you feeling right now?</p>

                <div className="mood-scale">
                    {moodEmojis.map((item) => (
                        <button
                            key={item.value}
                            className={`mood-button ${valence === item.value ? 'selected' : ''}`}
                            onClick={() => setValence(item.value)}
                        >
                            <span className="mood-emoji">{item.emoji}</span>
                            <span className="mood-label">{item.label}</span>
                        </button>
                    ))}
                </div>

                {valence !== null && (
                    <div className="mood-tags-section">
                        <p>Any specifics?</p>
                        <div className="tags-container">
                            {commonTags.map(tag => (
                                <button
                                    key={tag}
                                    className={`tag-button ${selectedTags.includes(tag) ? 'selected' : ''}`}
                                    onClick={() => toggleTag(tag)}
                                >
                                    {tag}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <div className="mood-actions">
                    <button className="skip-button" onClick={onComplete}>Skip</button>
                    <button
                        className="submit-button"
                        disabled={valence === null || isSubmitting}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? 'Saving...' : 'Save Mood'}
                    </button>
                </div>
            </div>
        </div>
    );
};
